import React from 'react';
import { useQuery } from '@tanstack/react-query';
import { TopicRequestBuilder } from 'src/components/BeaconV2Components/ExperimentalLayout/Traffic/utils/TopicRequestBuilder';
import { useBaseTopicRequestBuilder } from 'src/components/BeaconV2Components/ExperimentalLayout/Traffic/utils/useBaseTopicRequestBuilder';
import { Text } from 'src/components/BeaconV2Components/Generic/Text';
import { useAppSelector } from 'src/utils/Hooks';
import { useRetailerId } from 'src/utils/Hooks/reduxSelectors';
import axios from 'axios';
import EntityTable from 'src/components/BeaconV2Components/EntityTableRefresh/EntityTable';
import MetricCell from 'src/components/BeaconV2Components/MetricCell/MetricCell';
import { METRICTYPE } from 'src/utils/entityDefinitions';
import { calculatePercentChange } from 'src/utils/app';
import { GetAdKeywordMetricsResponse, GetAdKeywordMetricsResponseRecord } from './types';
import { TOPIC_QUERY_KEYS } from 'src/components/BeaconV2Components/ExperimentalLayout/Traffic/Settings/topicQueryKeys';

/**
 * Shows paid search keywords for topics
 */
export function PaidSearchKeywordGrid() {
  const { startWeek, endWeek } = useAppSelector((state) => state.mainTimePeriod);
  const { startWeek: comparisonStartWeek, endWeek: comparisonEndWeek } = useAppSelector(
    (state) => state.comparisonTimePeriod
  );
  const retailerId = useRetailerId();
  const baseRequest = useBaseTopicRequestBuilder();

  const adKeywordMetricsRequest = baseRequest
    .setRetailerId(+retailerId)
    .setSortField('adClicks')
    .setStartWeekId(startWeek)
    .setEndWeekId(endWeek)
    .build();

  const adKeywordMetricsComparisonRequest = new TopicRequestBuilder(adKeywordMetricsRequest)
    .setStartWeekId(comparisonStartWeek)
    .setEndWeekId(comparisonEndWeek)
    .build();

  const { data, isLoading } = useQuery({
    queryKey: [
      TOPIC_QUERY_KEYS.all,
      'PaidSearchTopicsKeywordGrid',
      adKeywordMetricsRequest,
      adKeywordMetricsComparisonRequest
    ],
    queryFn: async () => {
      const endpoint = '/api/beacontopics/GetAdKeywordMetrics';
      const [{ data: response }, { data: comparisonResponse }] = await Promise.all([
        axios.post<GetAdKeywordMetricsResponse>(endpoint, adKeywordMetricsRequest),
        axios.post<GetAdKeywordMetricsResponse>(endpoint, adKeywordMetricsComparisonRequest)
      ]);

      return [response, comparisonResponse];
    }
  });

  const [response, comparisonResponse] = data ?? [{ records: [] }, { records: [] }];

  const comparisonKeywordToMetricMap = comparisonResponse.records.reduce((acc, record) => {
    acc.set(record.keyword, record);
    return acc;
  }, new Map<string, GetAdKeywordMetricsResponseRecord>());

  return (
    <EntityTable
      columnDefs={[
        {
          headerName: 'Keywords',
          field: 'keyword'
        },
        {
          headerName: 'Campaigns',
          field: 'campaigns',
          valueFormatter: (value: number, row: GetAdKeywordMetricsResponseRecord) => {
            const comparisonValue = comparisonKeywordToMetricMap.get(row.keyword)?.campaigns ?? 0;
            const delta = value - comparisonValue;

            return (
              <MetricCell
                metricType={METRICTYPE.VOLUME}
                value={value}
                comparisonNode={
                  <Text color={delta >= 0 ? 'success' : 'error'}>{`(${delta >= 0 ? '+' : ''}${delta})`}</Text>
                }
              />
            );
          }
        },
        ...(
          [
            ['adClicks', 'Ad Clicks', METRICTYPE.VOLUME],
            ['adSpend', 'Ad Spend', METRICTYPE.MONEY],
            ['adSales', 'Ad Sales', METRICTYPE.MONEY],
            ['adUnitsSold', 'Ad Units Sold', METRICTYPE.VOLUME],
            ['roas', 'ROAS', METRICTYPE.MONEY]
          ] as const
        ).map(([field, title, metricType]) => {
          return {
            headerName: title,
            field,
            valueFormatter: (value, row: GetAdKeywordMetricsResponseRecord) => {
              const comparisonValue = comparisonKeywordToMetricMap.get(row.keyword)?.[field] ?? 0;

              return (
                <MetricCell
                  metricType={metricType}
                  value={value}
                  percentChange={calculatePercentChange(value, comparisonValue)}
                />
              );
            }
          };
        })
      ]}
      
      rowData={response.records}
      shouldModifyColumnDefs={false}
      isLoading={isLoading}
    />
  );
}
